import type { RtiComparisonGroupModel } from 'model/interfaces/rti-comparison-group';
import type { RtiEmployeeDataFields, RtiFieldSection } from 'model/interfaces/rti-employee-data-fields';
import { isCustomFieldValue } from 'utilities/rti-employee-data-fields';
import {
  applyDefaultJobCompanyValuesPreservingKeyOrder,
  normalizeRtiComparisonGroup,
} from 'utilities/rti-comparison-group';
import { normalizeRtiAggregatedCompensation } from 'utilities/rti-aggregated-compensation';
import { normalizeRtiEmployeeDataFields } from 'utilities/rti-employee-data-fields';

const EDF_SECTION_KEYS: (keyof RtiEmployeeDataFields)[] = [
  'employee_information',
  'job_information',
  'location',
  'compensation',
];

export function fieldSectionHasAtLeastOneSelected(section: RtiFieldSection): boolean {
  for (const leaf of Object.values(section)) {
    if (typeof leaf === 'boolean') {
      if (leaf === true) return true;
      continue;
    }
    if (isCustomFieldValue(leaf) && leaf.enabled === true) return true;
  }
  return false;
}

export function employeeDataFieldsHasAtLeastOneSelected(fields: RtiEmployeeDataFields): boolean {
  for (const key of EDF_SECTION_KEYS) {
    if (fieldSectionHasAtLeastOneSelected(fields[key] ?? {})) return true;
  }
  return false;
}

export function comparisonGroupHasAtLeastOneSelected(model: RtiComparisonGroupModel): boolean {
  return fieldSectionHasAtLeastOneSelected(model.job_company_information ?? {});
}

export function ensureRtiEmployeeDataFieldsSelection(raw: unknown): RtiEmployeeDataFields {
  const normalized = normalizeRtiEmployeeDataFields(raw);
  if (employeeDataFieldsHasAtLeastOneSelected(normalized)) return normalized;
  return normalizeRtiEmployeeDataFields(null);
}

export function ensureRtiAggregatedCompensationSelection(section: RtiFieldSection): RtiFieldSection {
  const normalized = normalizeRtiAggregatedCompensation(section);
  if (fieldSectionHasAtLeastOneSelected(normalized)) return normalized;
  return normalizeRtiAggregatedCompensation(null);
}

export function ensureRtiComparisonGroupSelection(raw: unknown): RtiComparisonGroupModel {
  const normalized = normalizeRtiComparisonGroup(raw);
  if (comparisonGroupHasAtLeastOneSelected(normalized)) {
    return normalized;
  }
  return {
    job_company_information: applyDefaultJobCompanyValuesPreservingKeyOrder(
      normalized.job_company_information,
    ),
  };
}

export function isRtiTemplateFieldSelectionValid(args: {
  employee_data_fields: RtiEmployeeDataFields;
  company_wide_report: unknown;
  comparison_group: unknown;
}): boolean {
  const edf = normalizeRtiEmployeeDataFields(args.employee_data_fields);
  if (!employeeDataFieldsHasAtLeastOneSelected(edf)) return false;
  const cwr = args.company_wide_report;
  const comp =
    cwr && typeof cwr === 'object' && !Array.isArray(cwr)
      ? (cwr as Record<string, unknown>).compensation
      : undefined;
  const agg = normalizeRtiAggregatedCompensation(comp);
  if (!fieldSectionHasAtLeastOneSelected(agg)) return false;
  const cg = normalizeRtiComparisonGroup(args.comparison_group);
  if (!comparisonGroupHasAtLeastOneSelected(cg)) return false;
  return true;
}
